import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild, ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import mermaid, {MermaidConfig} from 'mermaid';
import {BackendService} from '../../../services/general/backend.service';
import {SurveyService} from '../../../services/surveys.service';
import {Router} from '@angular/router';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {TranslateService} from '@ngx-translate/core';
import {lowerCase} from 'lodash';

import {MatDialog} from '@angular/material/dialog';
import {ModalSurveyRevisionPreviewComponent} from '../../../modals/modal-survey-revision-preview/modal-survey-revision-preview.component';
import {ModalSplitQuestionComponent} from '../../../modals/modal-split-question/modal-split-question.component';
import {ModalReviseQuestionComponent} from '../../../modals/modal-revise-question/modal-revise-question.component';
import {UserService} from '../../../services/user.service';

@Component({
    selector: 'app-survey-dashboard',
    templateUrl: './survey-dashboard.component.html',
    styleUrls: ['./survey-dashboard.component.scss'],
    standalone: false
})
export class SurveyDashboardComponent implements OnInit {

  constructor(
    protected surveyService: SurveyService,
    private router: Router,
    private translateService: TranslateService,
    private dialog: MatDialog,
    public userService: UserService
  ) {
  }

  @Input() survey: any;

  surveyDashboard: { [key: string]: DashboardItem } = {};
  loading = false;
  surveyCharts: { [key: string]: any } = {};
  previewLoading = false;

  consensusDisplayedColumns: string[] = [
    'statement',
    'votes',
    'consensus_indexes',
    'status',
    'hint',
    'ai_revision_proposal',
    'final_decision'
  ];

  @ViewChildren(MatPaginator) paginators!: QueryList<MatPaginator>;

  filters = {
    certainty: [0, 100],
    contradiction: [0, 100],
    agree: [0, 100],
    disagree: [0, 100],
    section: '',
    preliminary_round_assessment: [],
  };

  /** Stato paginazione per ogni tipologia (key) */
  paginationState: { [key: string]: { pageIndex: number; pageSize: number } } = {};

  protected readonly objectKeys = Object.keys;

  get currentUserInTeam() {
    return this.survey?.members?.find(
      (member: any) => member?.user_id == this.userService.userDetails?.user_id,
    );
  }

  ngOnInit(): void {
    this.loading = true;
    this.surveyService.getSurveyDashboard(
      this.survey?.id,
      (response) => {
        Object.keys(response).forEach((key) => {
          response[key].questions = new MatTableDataSource(response[key].questions || []);

          response[key].questions.filterPredicate = (q: any, filter: string) => {
            const filters = JSON.parse(filter);

            const certaintyOk =
              q.certainty_degree >= filters.certainty[0] &&
              q.certainty_degree <= filters.certainty[1];

            const agreeOk =
              q.agree_perc >= filters.agree[0] &&
              q.agree_perc <= filters.agree[1];

            const disagreeOk =
              q.disagree_perc >= filters.disagree[0] &&
              q.disagree_perc <= filters.disagree[1];

            const contradictionOk =
              q.contradiction_degree >= filters.contradiction[0] &&
              q.contradiction_degree <= filters.contradiction[1];

            const sectionOk =
              !filters.section || q.section === filters.section;

            const statusOk =
              filters.preliminary_round_assessment?.length === 0 ||
              filters.preliminary_round_assessment?.some((st) => st == q.status);

            return certaintyOk && contradictionOk && sectionOk && statusOk && disagreeOk && agreeOk;
          };

          if (key === 'CONSENSUS') {
            this.surveyCharts[key] = this.getConsensusPieChartOption(response[key].totals);
          }
        });

        this.surveyDashboard = response;
        this.loading = false;
      },
      (err) => {
        this.loading = false;
      }
    );
  }

  getConsensusPieChartOption(totals: ConsensusTotals): any {

    const toRevisionCount = Object.values(totals.TO_REVISION)
      .reduce((sum, t) => sum + t.count, 0);

    return {
      legend: {
        top: '0%',
        left: 'center',
        data: [
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.accepted'),
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.rejected'),
          this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision')
        ]
      },
      tooltip: {
        trigger: 'item',
        extraCssText: 'width: 500px; max-width: 800px; font-size: 14px;',
        formatter: (params: any) => {
          if (params.name !== this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision')) {
            return `${params.seriesName}<br/>${params.name} : ${params.value} (${params.percent}%)`;
          }

          const rows = Object.entries(totals.TO_REVISION)
            .map(([type, obj]) => `
        <div class="d-flex justify-content-between py-1">
          <div style="width: 33%;">
            ${this.translateService.instant(
              'components.surveys.survey-dashboard.survey-dashboard.component.' + type.toLowerCase()
            )}
          </div>
          <div class="text-center" style="width: 33%;">${obj.count}</div>
          <div class="text-end" style="width: 33%;">${obj.perc}%</div>
        </div>
      `).join('');

          return `
      <div style="min-width: 500px;">
        <div class="d-flex justify-content-between py-1 fw-bold">
          <div style="width: 33%;">${this.translateService.instant('generic.type')}</div>
          <div class="text-center" style="width: 33%;">${this.translateService.instant('generic.number')}</div>
          <div class="text-end" style="width: 33%;">%</div>
        </div>

        ${rows}
      </div>
    `;
        }
      },
      series: [
        {
          name: 'Consensus',
          type: 'pie',
          radius: '50%',
          center: ['50%', '50%'],
          selectedMode: 'single',
          data: [
            {
              value: totals.ACCEPTED.count,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.accepted'),
              itemStyle: {color: '#27ae60'}
            },
            {
              value: totals.REJECTED.count,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.rejected'),
              itemStyle: {color: '#e57373'}
            },
            {
              value: toRevisionCount,
              name: this.translateService.instant('components.surveys.survey-dashboard.survey-dashboard.component.to_revision'),
              itemStyle: {color: '#ffb74d'}
            }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  }

  formatPercent(value: number): string {
    return value + '%';
  }

  updateFilter(key: string, type: string, value: number, index: number) {
    this.filters[type][index] = value;
    this.surveyDashboard[key].questions.filter = JSON.stringify(this.filters);
  }

  updateSectionFilter(key: string, value: string) {
    this.filters.section = value;
    this.surveyDashboard[key].questions.filter = JSON.stringify(this.filters);
  }

  updateStatusFilter(key: string, status: []) {
    this.filters.preliminary_round_assessment = status;
    this.surveyDashboard[key].questions.filter = JSON.stringify(this.filters);
  }

  keyQuestionsSections(key): string[] {
    const data = this.surveyDashboard[key]?.questions?.data || [];
    const sections = data.map((q: any) => q.section).filter((s: any) => !!s);
    return Array.from(new Set(sections));
  }

  keyQuestionsStatuses(key): string[] {
    const data = this.surveyDashboard[key]?.questions?.data || [];
    const sections = data.map((q: any) => q.status).filter((s: any) => !!s);
    return Array.from(new Set(sections));
  }

  /** Dati filtrati per le card (stesso risultato dei filtri sulla tabella) */
  getFilteredQuestions(key: string): any[] {
    const ds = this.surveyDashboard[key]?.questions;
    if (!ds) return [];
    return (ds as any).filteredData ?? ds.data ?? [];
  }

  /** Dati paginati per le card */
  getPaginatedQuestions(key: string): any[] {
    const filtered = this.getFilteredQuestions(key);
    const { pageIndex, pageSize } = this.getPaginationState(key);
    const start = pageIndex * pageSize;
    return filtered.slice(start, start + pageSize);
  }

  getPaginationState(key: string): { pageIndex: number; pageSize: number } {
    if (!this.paginationState[key]) {
      this.paginationState[key] = { pageIndex: 0, pageSize: 10 };
    }
    const filteredLen = this.getFilteredQuestions(key).length;
    const maxPage = Math.max(0, Math.ceil(filteredLen / this.paginationState[key].pageSize) - 1);
    if (this.paginationState[key].pageIndex > maxPage) {
      this.paginationState[key].pageIndex = maxPage;
    }
    return this.paginationState[key];
  }

  onPageChange(key: string, event: PageEvent): void {
    this.paginationState[key] = {
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
    };
  }

  previewSurveyRevision() {
    this.previewLoading = true;
    this.surveyService.getSurveyRevisionPreview(this.survey.id)
      .subscribe({
        next: (response) => {
          response.survey_is_closed = this.survey.status === 'CLOSED';
          this.dialog.open(ModalSurveyRevisionPreviewComponent, {
            data: response,
            panelClass: 'medium-dialog',
          });
        },
        error: (err) => {
          console.error(err);
        },
        complete: () => {
          this.previewLoading = false;
        }
      });
  }

  setFinalDecision(question: any, decision: any) {
    if (decision === 'revised_split' || decision === 'revised') {
      const questionTmp = this.survey.questions.find((q: any) => q.id === question.id);


      if (decision === 'revised_split') {
        const dialogRef = this.dialog.open(ModalSplitQuestionComponent, {
          data: {question: questionTmp},
          panelClass: 'medium-dialog',
          disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            if (result?.cancelled) {
              question.final_decision = null;
              this.updateDecisionWithData(question, null);
            } else {
              question.final_decision = result.decision;
              questionTmp.customs.split_questions = result;
              this.updateDecisionWithData(question, decision, {split_questions: result});
            }
          }
        });
        return;
      } else if (decision === 'revised') {
        const dialogRef = this.dialog.open(ModalReviseQuestionComponent, {
          data: {question: questionTmp},
          panelClass: 'medium-dialog',
          disableClose: true
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            if (result?.cancelled) {
              question.final_decision = null;
              this.updateDecisionWithData(question, null);
            } else {
              question.final_decision = result.decision;
              questionTmp.customs.revision_question = result;
              this.updateDecisionWithData(question, decision, {revision_question: result});
            }
          }
        });
        return;
      }
    } else {
      if (decision == question.final_decision) {
        decision = null;
      }
    }

    this.updateDecisionWithData(question, decision);
  }

  updateDecisionWithData(question: any, decision: any, extraData: any = {}) {
    const question_tmp = {...question};
    const survey_question = this.survey?.questions?.find((q: any) => q.id === question.id);

    question.final_decision = decision;
    survey_question.final_decision = decision;

    const model = {
      final_decision: question.final_decision,
      ...extraData
    };

    this.surveyService.updateQuestionFinalDecision(
      this.survey.id,
      question.id,
      model,
      () => {
      },
      (err) => {
        question.final_decision = question_tmp.final_decision;
        survey_question.final_decision = question_tmp.final_decision;
      }
    );
  }
}

interface DashboardItem {
  questions: MatTableDataSource<any>;

  [key: string]: any;
}

interface ToRevisionType {
  [key: string]: { count: number; perc: number };
}

interface ConsensusTotals {
  ACCEPTED: { count: number; perc: number };
  REJECTED: { count: number; perc: number };
  TO_REVISION: ToRevisionType;
}
